<template>
  <div v-if="showQuestion">
    <div v-if="isCustom">
      <keep-alive v-if="customComponent">
        <component
          :is="customComponent"
          v-model="modelValue"
          :required="question?.required"
          :disabled="isFinished"
          v-bind="question"
        />
      </keep-alive>
    </div>
    <basic-question
      v-else
      v-model="modelValue"
      :required="question?.required"
      :titleProps="titleProps"
      :disabled="isFinished"
      v-bind="question"
    />
  </div>
</template>

<script>
import { WatchOptionsWithHandler, Component } from 'vue'
import { PropsDefinition, Computed } from '@/_typedefs'
import { createNamespacedHelpers } from 'vuex'
import { TitleProps } from '../Title/TitleNode.vue'
import BasicQuestion from '../../Questions/Basic/BasicQuestion.vue'
import AsthmaQuestionVue from '../../Asthma/Questions/AsthmaQuestion.vue'
import prefilledFunctions from '../../Questions/Computed/prefilled'
import conditionFunctions from '../../Questions/Computed/conditions'
import sideEffectFunctions from '../../Questions/Computed/sideEffects'
import AtopicDermatitisQuestion
  from '@/components/NewAttendanceForm/AtopicDermatitis/Questions/AtopicDermatitisQuestion.vue'

const {
  mapActions: newAttendanceFormActions,
  mapMutations: newAttendanceFormMutations,
  mapGetters: newAttendanceFormGetters,
} = createNamespacedHelpers('newAttendanceForm')

/**
 * @typedef {Object} Question
 * @property {string} title
 * @property {string} kind
 * @property {boolean} [isCustom]
 * @property {object} questionData
 * @property {boolean} required
 */

/**
 * @typedef {Object} QuestionNodeProps
 * @property {TitleProps} titleProps
 * @property {boolean} required
 * @property {string} questionKey
 */

export default {
  components: { BasicQuestion },
  name: 'QuestionNode',
  /** @type {PropsDefinition<QuestionNodeProps>} */
  props: {
    titleProps: {
      type: Object,
      default: null,
    },
    questionKey: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      /** @type {Question | null} */
      question: null,
      /** @type {any} */
      modelValue: null,
      /** @type {Array<(value: any) => object>} */
      conditions: [],
      /** @type {Function?} */
      sideEffectUnwatch: null,
    }
  },
  computed: {
    .../** @type {{isFinished: Computed<boolean>}} */ (newAttendanceFormGetters(['isFinished', 'questions'])),
    /** @type {Computed<boolean>} */
    isCustom() {
      if (!this.question) {
        return false
      }
      return this.question.isCustom
    },
    /** @type {Computed<Component | null>} */
    customComponent() {
      if (!this.isCustom) {
        return null
      }

      const [disease, _] = this.question.kind.split(':')

      const diseaseMap = {
        asthma: AsthmaQuestionVue,
        'atopic-dermatitis': AtopicDermatitisQuestion,
      }

      const component = diseaseMap[disease] ?? null
      if (!component) {
        return null
      }

      return component
    },
    showQuestion() {
      return this.question && (this.questions[this.question.questionKey].show ?? true)
    },
  },
  methods: {
    ...newAttendanceFormActions(['getQuestion', 'getAnswer']),
    ...newAttendanceFormMutations(['updateAnswer', 'addSideEffect', 'updateSideEffectStatus', 'setShowWhen', 'setQuestions']),
    openQuestion() {
      this.$emit('open-expandable-box')
    },
    updateVisibility(value) {
      const children = this.question.questionData.children ?? []
      children.forEach((c) => {
        const question = this.questions[c.key]

        switch (c.condition) {
          case 'IS':
            question.show = value === c.value
            break
          default:
            break
        }
        this.setQuestions({ ...this.questions, ...question })
      })
    },
  },
  async created() {
    const [question, answer] = await Promise.all([this.getQuestion(this.questionKey), this.getAnswer(this.questionKey)])

    this.question = question
    this.modelValue = answer
    this.updateVisibility(answer)

    this.$eventBus.on(`open-question-${this.questionKey}`, this.openQuestion)

    const { prefilledBy } = this.question?.questionData ?? {}
    if (prefilledBy in prefilledFunctions && !this.modelValue) {
      this.modelValue = prefilledFunctions[prefilledBy]()
    }

    const { hint } = this.question?.questionData ?? {}
    if (hint?.condition && hint.condition.fn in conditionFunctions) {
      const conditionFn = conditionFunctions[hint.condition.fn]
      const conditionArgs = hint.condition.args.map((arg) => JSON.parse(arg))
      this.conditions.push((value) => {
        const res = conditionFn(value, ...conditionArgs)
        return { hint: res ? hint.text : null }
      })
    }

    const { sideEffect } = this.question?.questionData ?? {}
    if (sideEffect in sideEffectFunctions) {
      const sideEffectFn = sideEffectFunctions[sideEffect](this.questionKey)
      this.addSideEffect({ questionKey: this.questionKey, callback: sideEffectFn })
    }

    const conditionUpdates = this.conditions.reduce(
      (acc, condition) => ({
        ...acc,
        ...condition(this.modelValue),
      }),
      {},
    )

    this.question.questionData = {
      ...this.question.questionData,
      ...conditionUpdates,
    }

    this.$eventBus.emit(`question-${this.questionKey}-loaded`)
  },
  watch: {
    /** @type {WatchOptionsWithHandler<any>} */
    modelValue: {
      handler(value) {
        this.updateAnswer({ questionKey: this.questionKey, value })
        this.updateSideEffectStatus({ questionKey: this.questionKey, shouldRun: true })
        this.updateVisibility(value)
        const conditionUpdates = this.conditions.reduce(
          (acc, condition) => ({
            ...acc,
            ...condition(value),
          }),
          {},
        )

        this.question.questionData = {
          ...this.question.questionData,
          ...conditionUpdates,
        }
      },
      deep: true,
    },
  },
  beforeDestroy() {
    this.$eventBus.off(`open-${this.questionKey}`, this.openQuestion)
  },
}
</script>

<style></style>
